/**
 * Belegart — document type of an invoice record.
 *
 * Stored values are stable English keys; display labels are derived (mixed EN/DE
 * audience, German accounting terms in parentheses). Default is `invoice`.
 * `credit_note` (Gutschrift) and `cancellation` (Storno) typically carry negative
 * amounts and reference an original invoice — but the sign is entered manually
 * (see signMismatch), not auto-negated.
 */
export type DocumentType = 'invoice' | 'credit_note' | 'cancellation'

export const DOCUMENT_TYPES: DocumentType[] = ['invoice', 'credit_note', 'cancellation']

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  invoice: 'Invoice',
  credit_note: 'Credit Note',
  cancellation: 'Cancellation',
}

/** MUI Chip colors per document type. */
export const DOCUMENT_TYPE_COLORS: Record<DocumentType, 'default' | 'secondary' | 'warning'> = {
  invoice: 'default',
  credit_note: 'secondary',
  cancellation: 'warning',
}

export const DOCUMENT_TYPE_OPTIONS = DOCUMENT_TYPES.map((value) => ({
  value,
  label: DOCUMENT_TYPE_LABELS[value],
}))

/** Coalesce an unknown/legacy value to a valid document type (default: invoice). */
export function normalizeDocumentType(value: string | null | undefined): DocumentType {
  return value === 'credit_note' || value === 'cancellation' ? value : 'invoice'
}

/** Only normal invoices count toward due-date / overdue logic and duplicate checks. */
export function countsAsPayable(documentType: string | null | undefined): boolean {
  return normalizeDocumentType(documentType) === 'invoice'
}

/**
 * Plausibility check (non-blocking): credit notes / cancellations are usually
 * negative, normal invoices positive. Returns a warning string when the gross
 * amount's sign contradicts the document type, else null. Sign is NEVER changed
 * automatically — this only nudges the user.
 */
export function signMismatchWarning(
  documentType: string | null | undefined,
  gross: number | null | undefined,
): string | null {
  if (gross == null || gross === 0) return null
  const dt = normalizeDocumentType(documentType)
  if ((dt === 'credit_note' || dt === 'cancellation') && gross > 0) {
    return `${DOCUMENT_TYPE_LABELS[dt]} is usually negative — is the amount sign correct?`
  }
  if (dt === 'invoice' && gross < 0) {
    return 'Invoice is usually positive — is the amount sign correct?'
  }
  return null
}
